import React, { useState, useEffect, useMemo } from 'react';
import { FaFlag, FaTrash, FaCheck, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';
import { getAssetUrl } from '../../../utils/apiBase';
import { useAuth } from '../../../contexts/AuthContext';
import { Button, Alert, Modal, Row, Col, Spinner } from 'react-bootstrap';

const ForumModeration = () => {
  const { user } = useAuth();
  const [highlights, setHighlights] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedHighlight, setSelectedHighlight] = useState(null);
  const [moderating, setModerating] = useState(false);
  const [statusCounts, setStatusCounts] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [highlightToModerate, setHighlightToModerate] = useState(null);

  useEffect(() => {
    fetchHighlightedComments();
  }, [currentPage]);

  const fetchHighlightedComments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.forums.getHighlightedComments({
        page: currentPage,
        limit: 20
      });
      
      if (response.data.success) {
        setHighlights(response.data.data.highlights);
        setTotalPages(response.data.data.totalPages);
        setStatusCounts(response.data.data.counts || null);
      } else {
        setError(response.data.message || 'Failed to fetch highlighted comments');
        setStatusCounts(null);
      }
    } catch (error) {
      console.error('Error fetching highlighted comments:', error);
      setError('Failed to fetch highlighted comments. Please try again.');
      toast.error('Failed to fetch highlighted comments');
      setStatusCounts(null);
    } finally {
      setLoading(false);
    }
  };

  const handleModerateClick = (highlight, action) => {
    setHighlightToModerate(highlight);
    setConfirmAction(action);
    setShowConfirmModal(true);
    setShowDetailModal(false);
  };

  const handleModerateConfirm = async () => {
    if (!highlightToModerate || !confirmAction) return;

    try {
      setModerating(true);
      const response = await api.forums.moderateHighlightedComment(highlightToModerate.id, confirmAction);
      
      if (response.data.success) {
        toast.success(`Comment ${confirmAction}d successfully`);
        fetchHighlightedComments();
        setShowConfirmModal(false);
        setHighlightToModerate(null);
        setConfirmAction(null);
        setShowDetailModal(false);
        setSelectedHighlight(null);
      } else {
        toast.error(response.data.message || `Failed to ${confirmAction} comment`);
      }
    } catch (error) {
      console.error(`Error ${confirmAction}ing comment:`, error);
      toast.error(`Failed to ${confirmAction} comment`);
    } finally {
      setModerating(false);
    }
  };

  const handleModerateCancel = () => {
    setShowConfirmModal(false);
    setHighlightToModerate(null);
    setConfirmAction(null);
  };

  const handleViewDetails = (highlight) => {
    setSelectedHighlight(highlight);
    setShowDetailModal(true);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Pending</span>;
      case 'approved':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">Approved (Deleted)</span>;
      case 'ignored':
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Ignored</span>;
      default:
        return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-50 text-gray-700">Unknown</span>;
    }
  };

  const statusSummary = useMemo(() => {
    if (statusCounts) {
      return {
        pending: statusCounts.pending ?? 0,
        approved: statusCounts.approved ?? 0,
        ignored: statusCounts.ignored ?? 0
      };
    }

    const summary = {
      pending: 0,
      approved: 0,
      ignored: 0
    };

    highlights.forEach((highlight) => {
      if (highlight.status && summary[highlight.status] !== undefined) {
        summary[highlight.status] += 1;
      }
    });

    return summary;
  }, [statusCounts, highlights]);

  const totalFlagged = useMemo(() => (
    statusSummary.pending + statusSummary.approved + statusSummary.ignored
  ), [statusSummary]);

  if (loading) {
    return (
      <div className="min-h-[24rem] flex items-center justify-center">
        <svg className="animate-spin h-10 w-10 text-purple-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
        </svg>
      </div>
    );
  }

  return (
    <div className="p-3 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <div className="mb-2">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="mb-1">
            <h3 className="fw-bold text-xl sm:text-2xl md:text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">Flagged Comments</h3>
          </div>
          <div className="flex items-center gap-2">
            <Button
              bsPrefix="custom-hamburger"
              onClick={fetchHighlightedComments}
              disabled={loading}
              className="border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 py-2 px-8"
            >
              <FaFlag />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-4">
          <small className="text-xs uppercase text-gray-500 font-semibold">Total flagged</small>
          <div className="mt-2">
            <div className="text-3xl font-bold text-red-600">{totalFlagged}</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <small className="text-xs uppercase text-gray-500 font-semibold">Pending review</small>
          <div className="mt-2">
            <div className="text-3xl font-bold text-yellow-500">{statusSummary.pending}</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <small className="text-xs uppercase text-gray-500 font-semibold">Approved</small>
          <div className="mt-2">
            <div className="text-3xl font-bold text-green-600">{statusSummary.approved}</div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <small className="text-xs uppercase text-gray-500 font-semibold">Ignored</small>
          <div className="mt-2">
            <div className="text-3xl font-bold text-gray-500">{statusSummary.ignored}</div>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}

      {highlights.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FaFlag size={48} className="mx-auto text-gray-400 mb-3" />
          <h5 className="text-gray-600">No flagged comments</h5>
          <p className="text-gray-400">All comments are clean and appropriate.</p>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="p-4 border-b">
              <h4 className="text-lg font-semibold">Flagged Comments ({highlights.length})</h4>
            </div>
            <div className="overflow-x-auto -mx-3 sm:mx-0 rounded-xl sm:rounded-2xl shadow-lg bg-white">
              <table className="m-0 min-w-max text-xs sm:text-sm table table-bordered table-hover">
                <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                  <tr>
                    <th className="px-3 py-2 text-left">Comment</th>
                    <th className="px-3 py-2 text-left">Forum</th>
                    <th className="px-3 py-2 text-left">Author</th>
                    <th className="px-3 py-2 text-left">Flagged By</th>
                    <th className="px-3 py-2 text-left">Reason</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {highlights.map(highlight => (
                    <tr key={highlight.id} className="align-top border-b last:border-b-0">
                      <td className="px-3 py-3" style={{ maxWidth: 320 }}>
                        <div>
                          <p className="mb-1 text-sm text-gray-700 line-clamp-2">{highlight.comment?.content}</p>
                          <small className="text-xs text-gray-400">{formatDate(highlight.comment?.createdAt)}</small>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <strong className="block text-sm text-gray-800 truncate">{highlight.comment?.forum?.title}</strong>
                          <small className="text-xs text-gray-400">{highlight.comment?.forum?.org_id ? 'Organization' : 'Global'}</small>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <strong className="block text-sm text-gray-800">{highlight.comment?.user?.first_name} {highlight.comment?.user?.last_name}</strong>
                          <small className="text-xs text-gray-400">{highlight.comment?.user?.email}</small>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <div>
                          <strong className="block text-sm text-gray-800">{highlight.highlightedBy?.first_name} {highlight.highlightedBy?.last_name}</strong>
                          <small className="text-xs text-gray-400">{formatDate(highlight.createdAt)}</small>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <span className="text-sm text-gray-500">{highlight.reason || 'Marked as vulgar content'}</span>
                      </td>
                      <td className="px-3 py-3">{getStatusBadge(highlight.status)}</td>
                      <td className="px-3 py-3">
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            bsPrefix="custom-hamburger"
                            onClick={() => handleViewDetails(highlight)}
                            title="View Details"
                            className="border-gray-300 text-gray-600 hover:bg-gray-100 rounded p-1"
                          >
                            <FaEye className="w-5 h-5" />
                          </Button>
                          {highlight.status === 'pending' && (
                            <>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleModerateClick(highlight, 'approve')}
                                disabled={moderating}
                                title="Delete Comment"
                                className="border-red-300 text-red-600 hover:bg-red-50 rounded p-1"
                              >
                                <FaTrash className="w-5 h-5" />
                              </Button>
                              <Button
                                bsPrefix="custom-hamburger"
                                onClick={() => handleModerateClick(highlight, 'ignore')}
                                disabled={moderating}
                                title="Ignore Flag"
                                className="border-green-300 text-green-600 hover:bg-green-50 rounded p-1"
                              >
                                <FaCheck className="w-5 h-5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {totalPages > 1 && (
            <div className="flex justify-center">
              <nav>
                <ul className="flex items-center gap-2">
                  <li>
                    <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="px-3 py-2 rounded-md bg-white border border-gray-200 hover:shadow">Previous</button>
                  </li>
                  {[...Array(totalPages)].map((_, index) => (
                    <li key={index + 1}>
                      <button onClick={() => setCurrentPage(index + 1)} className={`px-3 py-2 rounded-md ${currentPage === index + 1 ? 'bg-purple-600 text-white' : 'bg-white border border-gray-200'}`}>{index + 1}</button>
                    </li>
                  ))}
                  <li>
                    <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="px-3 py-2 rounded-md bg-white border border-gray-200 hover:shadow">Next</button>
                  </li>
                </ul>
              </nav>
            </div>
          )}
        </div>
      )}

      {/* View Detail Modal */}
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg" centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Comment Details
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-gray-50 p-3 sm:p-5">
          {selectedHighlight && (
            <div className="space-y-4">
              <Row className="g-4 mb-4">
                <Col md={6}>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-700 mb-2">Comment Author</h6>
                    <p className="mb-1 text-sm font-semibold text-gray-800">{selectedHighlight.comment?.user?.first_name} {selectedHighlight.comment?.user?.last_name}</p>
                    <p className="text-xs text-gray-500">{selectedHighlight.comment?.user?.email}</p>
                  </div>
                </Col>
                <Col md={6}>
                  <div>
                    <h6 className="text-sm font-semibold text-gray-700 mb-2">Flagged By</h6>
                    <p className="mb-1 text-sm font-semibold text-gray-800">{selectedHighlight.highlightedBy?.first_name} {selectedHighlight.highlightedBy?.last_name}</p>
                    <p className="text-xs text-gray-500">{formatDate(selectedHighlight.createdAt)}</p>
                  </div>
                </Col>
              </Row>

              <div className="mb-4">
                <h6 className="text-sm font-semibold text-gray-700 mb-2">Forum</h6>
                <p className="mb-1 text-sm font-semibold text-gray-800">{selectedHighlight.comment?.forum?.title}</p>
                <div className="inline-block">
                  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${selectedHighlight.comment?.forum?.org_id ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`}>{selectedHighlight.comment?.forum?.org_id ? 'Organization' : 'Global'}</span>
                </div>
              </div>

              <div className="mb-4">
                <h6 className="text-sm font-semibold text-gray-700 mb-2">Comment Content</h6>
                <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                  <p className="text-sm whitespace-pre-wrap text-gray-800">{selectedHighlight.comment?.content}</p>
                  {selectedHighlight.comment?.attachment && (
                    <div className="mt-2">
                      <img src={getAssetUrl(selectedHighlight.comment.attachment)} alt="Comment attachment" className="max-w-[200px] max-h-[200px] object-cover rounded" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mb-4">
                <h6 className="text-sm font-semibold text-gray-700 mb-2">Flag Reason</h6>
                <p className="text-sm text-gray-600">{selectedHighlight.reason || 'Marked as vulgar content'}</p>
              </div>

              <div className="mb-4">
                <h6 className="text-sm font-semibold text-gray-700 mb-2">Status</h6>
                {getStatusBadge(selectedHighlight.status)}
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={selectedHighlight?.status === 'pending' ? 4 : 12}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={() => setShowDetailModal(false)}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Close
              </Button>
            </Col>
            {selectedHighlight?.status === 'pending' && (
              <>
                <Col md={4}>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={() => handleModerateClick(selectedHighlight, 'approve')}
                    disabled={moderating}
                    className="w-full border-1 border-red-600 bg-white text-red-600 hover:bg-red-50 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    Delete Comment
                  </Button>
                </Col>
                <Col md={4}>
                  <Button
                    bsPrefix="custom-hamburger"
                    onClick={() => handleModerateClick(selectedHighlight, 'ignore')}
                    disabled={moderating}
                    className="w-full border-1 border-green-600 bg-white text-green-600 hover:bg-green-50 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
                  >
                    Ignore Flag
                  </Button>
                </Col>
              </>
            )}
          </Row>
        </Modal.Footer>
      </Modal>

      {/* Approve/Delete Confirmation Modal */}
      <Modal show={showConfirmModal} onHide={handleModerateCancel} centered>
        <Modal.Header closeButton closeVariant="white" className="bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 py-3 px-4 sm:px-4 sm:py-4 modal-header">
          <Modal.Title className="font-bold text-xl text-white">
            Confirm {confirmAction === 'approve' ? 'Delete' : 'Ignore'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-center py-10 bg-gray-50">
          <div className="mb-4">
            {confirmAction === 'approve' ? (
              <FaTrash size={60} className="text-red-500 mx-auto" />
            ) : (
              <FaCheck size={60} className="text-green-500 mx-auto" />
            )}
          </div>
          <h5 className="font-bold text-gray-800 mb-3">
            {confirmAction === 'approve' ? 'Delete Comment' : 'Ignore Flag'}
          </h5>
          <p className="text-gray-600">
            Are you sure you want to {confirmAction === 'approve' ? 'delete' : 'ignore'} this flagged comment?
          </p>
          {highlightToModerate?.comment?.content && (
            <div className="mt-4 p-3 bg-gray-100 rounded-lg text-left">
              <p className="text-sm text-gray-700 line-clamp-3">
                "{highlightToModerate.comment.content.substring(0, 100)}{highlightToModerate.comment.content.length > 100 ? '...' : ''}"
              </p>
            </div>
          )}
          <p className="text-sm text-gray-500 mt-3">
            {confirmAction === 'approve' 
              ? 'This will permanently delete the comment. This action cannot be undone.' 
              : 'This will mark the flag as ignored. The comment will remain visible.'}
          </p>
        </Modal.Body>
        <Modal.Footer className="bg-gray-100 border-0 rounded-b-2xl p-4">
          <Row className="w-100 g-3">
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleModerateCancel}
                disabled={moderating}
                className="w-full border-1 bg-gray-100 hover:bg-gray-200 text-gray-700 border-2 border-gray-300 hover:border-gray-500 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2"
              >
                Cancel
              </Button>
            </Col>
            <Col md={6}>
              <Button
                bsPrefix="custom-hamburger"
                onClick={handleModerateConfirm}
                disabled={moderating}
                className={`w-full border-1 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2 ${
                  confirmAction === 'ae' 
                    ? 'w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2' 
                    : 'w-full border-1 border-purple-600 bg-white text-purple-600 hover:from-purple-700 hover:to-pink-700 font-semibold rounded-lg sm:rounded-xl shadow-md hover:shadow-lg transition-all duration-200 text-xs sm:text-sm h-full flex items-center justify-center gap-2 p-2'
                }`}
              >
                {moderating ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {confirmAction === 'approve' ? 'Deleting...' : 'Ignoring...'}
                  </>
                ) : (
                  confirmAction === 'approve' ? 'Delete' : 'Ignore'
                )}
              </Button>
            </Col>
          </Row>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ForumModeration;
